import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { ISearchResultRecord } from '../../SearchResultItem/models/ISearchResultRecord.js';
import { SearchResultItem } from '../../SearchResultItem/SearchResultItem.js';
import { ISearchConfiguration } from '../models';

const StoreBase = stores.base.StoreBase<IConfiguration, IComponentStyles>;
type QueryStore = stores.QueryStore<IConfiguration, IComponentStyles>;
type TranslationStore = stores.TranslationStore<IConfiguration, IComponentStyles>;
type ISearchProps = components.base.Search.models.ISearchProps;

const { isNullOrEmpty } = common.commonUtils;

export class SearchStore extends StoreBase {
	static componentKey: 'search' = 'search' as const;
	@observable shareClassCount = 0;
	@observable displayPill = false;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);

		makeObservable(this);
	}

	@computed
	get appStore(): AppStore {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get queryStore(): QueryStore {
		return this.storeContext.get<QueryStore>('queryStore');
	}

	@computed
	get translationStore(): TranslationStore {
		return this.storeContext.get<TranslationStore>('translationStore');
	}

	@action
	updateDisplayPill(display: boolean) {
		this.displayPill = display;
	}

	@computed
	get configuration(): ISearchConfiguration | undefined {
		if (this.storeContext && this.appStore) {
			return this.appStore.getComponentConfiguration(SearchStore.componentKey);
		}
	}

	@computed
	get show(): boolean {
		return !!this.appStore.redirectConfiguration;
	}

	@action
	async initialize(): Promise<void> {
		this.updateInitialTotalShareClasses();
	}

	search = async (term: string) => {
		if (this.configuration) {
			const { dataEntitiesId, shareClassCount, noResultText, resultItemLabel } =
				this.configuration.secondaryProps;
			const searchResponse = await this.queryStore.entitiesRequest(dataEntitiesId, { term });
			const placeholder = !isNullOrEmpty(shareClassCount.placeholder) ? shareClassCount.placeholder : 'count';
			if (searchResponse) {
				if (searchResponse.total > 0) {
					return {
						term,
						records: searchResponse.values.map((entity) => {
							const response: ISearchResultRecord = {
								id: entity.clientCode,
								value: {
									linkedEntity: entity,
								},
							};

							if (resultItemLabel.left) {
								response.value.left = resultItemLabel.left.map((item) =>
									this.getQueryValue(item, undefined, this.appStore.overrideEntityContext(entity)),
								);
							}

							if (resultItemLabel.right) {
								response.value.right = resultItemLabel.right.map((item) =>
									this.getQueryValue(item, undefined, this.appStore.overrideEntityContext(entity)),
								);
							}

							return response;
						}),
						pillProps: {
							value: this.translationStore.translate(shareClassCount.label, {
								[placeholder]: searchResponse.total,
							}),
						},
					};
				}

				return {
					term,
					records: [
						{
							id: 'noResults',
							value: {
								left: [
									this.translationStore.translate(
										!isNullOrEmpty(noResultText) ? noResultText : 'No Results found',
									),
								],
							},
						},
					],
					pillProps: {
						value: this.translationStore.translate(shareClassCount.label, {
							[placeholder]: searchResponse.total,
						}),
					},
				};
			}
		}
		return {
			term,
			records: [],
			pillProps: {
				value: `0 countries`,
			},
		};
	};

	onSelect = (item: any) => {
		if (item && item.id !== 'noResults' && item.value && item.value.linkedEntity) {
			let searchTerm: string | undefined;
			if (item.value.left) {
				searchTerm = item.value.left.join('; ');
			} else if (item.value.right) {
				searchTerm = item.value.right.join('; ');
			}
			this.appStore.redirect(item.value.linkedEntity, { event: 'search', searchTerm: searchTerm });
		}
	};

	@action
	async updateInitialTotalShareClasses() {
		if (this.configuration) {
			const { dataEntitiesId } = this.configuration.secondaryProps;
			const searchResponse = await this.queryStore.entitiesRequest(dataEntitiesId, { term: '' });
			runInAction(() => {
				this.shareClassCount = (searchResponse && searchResponse.total) || 0;
			});
		}
	}

	@computed
	get initialTotalShareClassCount(): string {
		if (this.configuration) {
			const { shareClassCount } = this.configuration.secondaryProps;
			const placeholder = shareClassCount.placeholder ? shareClassCount.placeholder : 'count';
			return this.translationStore.translate(shareClassCount.label, { [placeholder]: this.shareClassCount });
		}
		return '';
	}

	@computed
	get props(): ISearchProps {
		const shareClassCountText = this.initialTotalShareClassCount;

		let props = this.mergeQueriesAndProps(this.configuration) as ISearchProps;
		props = Object.assign(props, {
			searchCallback: this.search,
			onResultItemClick: this.onSelect,
			resultItemComponent: SearchResultItem,
		});

		if (this.displayPill && props.pillProps) {
			props.pillProps.value = shareClassCountText;
		} else if (!this.displayPill) {
			delete props.pillProps;
		}
		// props.hide = this.shareClassCount === 0;
		return props;
	}
}
